// src/components/forms/shared/BookingCardPetsList.tsx
// Displays booking pets with detailed line items (rates, nights, grooming, totals)
// Reuses SummaryRow component for consistency with BookingSummary

import React from 'react';
import { Box, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getCustomIcon } from '../../../utils/customIcons';
import SummaryRow from './SummaryRow';

interface BookingGroomingResponse {
  id: number;
  groomingType: string;
  price: number;
}

interface BookingPetResponse {
  id: number;
  petId: number;
  petName: string;
  petType: string;
  petBreed: string;
  petSize: string;
  useSharingRate: boolean;
  hasHeater: boolean;
  dailyRate: number;
  numberOfNights: number;
  accommodationTotal: number;
  groomingTotal: number;
  petTotal: number;
  lineItemDiscount?: number;
  lineItemDiscountPercentage?: number;
  rateType: string | null;
  grooming?: BookingGroomingResponse[];
}

interface BookingCardPetsListProps {
  pets: BookingPetResponse[];
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Format grooming label to match BookingSummary pattern
const formatGroomingLabel = (groomingType: string): string => {
  switch (groomingType) {
    case 'Smooth':
      return 'Groom Smooth';
    case 'Long':
      return 'Groom Long';
    case 'Cut':
      return 'Groom Cut';
    default:
      return groomingType;
  }
};

const BookingCardPetsList: React.FC<BookingCardPetsListProps> = ({ pets }) => {
  const theme = useTheme();
  if (pets.length === 0) return null;

  return (
    <Box sx={{ width: '100%', maxWidth: '800px' }}>
      {pets.map((pet, idx) => {
        // Build subrows for this pet
        const subRows: Array<{
          label: string | React.ReactNode;
          value?: string | React.ReactNode;
          backgroundColor?: string;
        }> = [];

        // Helper to calculate alternating background color
        const getRowBgColor = (rowIdx: number) =>
          rowIdx % 2 === 0
            ? 'transparent'
            : theme.palette.mode === 'dark'
              ? 'rgba(33, 150, 243, 0.08)'
              : 'rgba(0, 0, 0, 0.1)';

        let rowIdx = 0;

        // Rate per night
        subRows.push({
          label: 'Rate',
          value: `${pet.rateType} - R${pet.dailyRate.toFixed(2)} p/n`,
          backgroundColor: getRowBgColor(rowIdx++),
        });

        // Number of nights (convert to days for display: nights = days in booking context)
        const numberOfDays = pet.numberOfNights;
        subRows.push({
          label: `${numberOfDays} Day${numberOfDays !== 1 ? 's' : ''}`,
          value: formatCurrency(pet.accommodationTotal),
          backgroundColor: getRowBgColor(rowIdx++),
        });

        // Discount row (always shown)
        const discountAmount = pet.lineItemDiscount || 0;
        const discountPercentage = pet.lineItemDiscountPercentage || 0;
        subRows.push({
          label: 'Discount',
          value:
            discountAmount > 0
              ? `@ ${discountPercentage}% = -${formatCurrency(discountAmount)}`
              : 'R 0.00',
          backgroundColor: getRowBgColor(rowIdx++),
        });

        // After discount row (conditional - only if discount > 0)
        if (discountAmount > 0) {
          const afterDiscountAmount = pet.accommodationTotal - discountAmount;
          subRows.push({
            label: 'After discount',
            value: formatCurrency(afterDiscountAmount),
            backgroundColor: getRowBgColor(rowIdx++),
          });
        }

        // Individual grooming items (conditional - only if grooming exists and total > 0)
        // Group by type and consolidate duplicates with count
        if (pet.groomingTotal > 0 && pet.grooming && pet.grooming.length > 0) {
          // Group grooming items by type
          const groomingByType = new Map<
            string,
            { count: number; totalPrice: number }
          >();

          pet.grooming.forEach((item) => {
            const existing = groomingByType.get(item.groomingType) || {
              count: 0,
              totalPrice: 0,
            };
            groomingByType.set(item.groomingType, {
              count: existing.count + 1,
              totalPrice: existing.totalPrice + item.price,
            });
          });

          // Add consolidated grooming rows
          groomingByType.forEach((data, groomingType) => {
            const label = formatGroomingLabel(groomingType);
            const displayLabel =
              data.count > 1 ? `${label} x${data.count}` : label;
            subRows.push({
              label: displayLabel,
              value: formatCurrency(data.totalPrice),
              backgroundColor: getRowBgColor(rowIdx++),
            });
          });
        }

        // Grooming total (conditional - only if grooming total > 0)
        if (pet.groomingTotal > 0) {
          subRows.push({
            label: 'Grooming Cost',
            value: formatCurrency(pet.groomingTotal),
            backgroundColor: getRowBgColor(rowIdx++),
          });
        }

        // Final total (empty label, bold value)
        subRows.push({
          label: '',
          value: formatCurrency(pet.petTotal),
          // backgroundColor: getRowBgColor(rowIdx++),
        });

        return (
          <Box
            key={pet.id}
            sx={{
              marginBottom: 0,
              '& > div': {
                borderLeft: 'none',
                pl: 0,
                borderRadius: 0,
                '& > div > div': {
                  pl: '1rem',
                  pr: 1.5,
                },
                '@media (max-width: 600px)': {
                  pl: 0,
                  '& > div > div': {
                    pl: 1.3,
                    pr: 1.5,
                  },
                },
              },
            }}
          >
            {/*
            THIS THE ROW WITH ICON, PETNAME - TYPE, BREED :: css headerParams
            EVERY OTHER ROW IS A SUBROW FROM AN ARRAY
            */}
            <SummaryRow
              subRowBorderBottom={`2px dashed ${theme.palette.dividerContrast}`}
              headerBackgroundColor={theme.palette.primary.light}
              headerBorderTop={`4px solid ${theme.palette.primary.dark}`}
              headerPadding=".19rem 0 .5rem 0"
              totalRowBorderBottom={`1px solid ${theme.palette.primary.dark}`}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  {/* icon container */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {getCustomIcon(pet.petType, '2.5rem')}
                  </Box>
                  <span style={{ fontWeight: 600, fontSize: '2rem' }}>
                    {pet.petName}
                  </span>
                  <span style={{ fontWeight: 400 }}>
                    - {pet.petType}, {pet.petBreed}
                  </span>
                </Box>
              }
              value=""
              subRows={subRows}
              subRowLabelColor="text.primary"
            />
          </Box>
        );
      })}
    </Box>
  );
};

export default BookingCardPetsList;
